import { useEffect, useRef, useState } from "react";
import RightTimeApi from "@/API";
import { ImCancelCircle } from "react-icons/im";
import { FaPlusSquare } from "react-icons/fa";
import { IoTrashOutline } from "react-icons/io5";
import { toast } from "react-toastify";
import { MdOutlineImageNotSupported } from "react-icons/md";
import Modal from "@modalSrc/Modal";

function PickerImageThumbnail({ image, onSelect }) {
  const [loadFailed, setLoadFailed] = useState(false);

  if (image.present === false || loadFailed) {
    return (
      <MdOutlineImageNotSupported
        className="thumbnail imageUnavailable"
        size={48}
        title="Image not available"
        onClick={() => onSelect(image.id)}
        style={{ cursor: "pointer" }}
      />
    );
  }

  return (
    <img
      className="thumbnail"
      src={RightTimeApi.URL(`/Image/${image.id}`)}
      alt={image.fileName || image.name}
      onClick={() => onSelect(image.id)}
      onError={() => setLoadFailed(true)}
      style={{ cursor: "pointer" }}
    />
  );
}

export default function ImagePickerModal({
  isOpen,
  onClose,
  companyId,
  updateSpinner,
}) {
  const [images, setImages] = useState([]);
  const [selectedImageId, setSelectedImageId] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    setSelectedImageId(null);
    getImageAsync();
  }, [isOpen, companyId]);

  async function getImageAsync() {
    if (!companyId) return;
    const resp = await RightTimeApi.getAsync(`/CompanyImages/${companyId}`);
    if (resp?.ok && resp.jsonData) {
      setImages(Array.isArray(resp.jsonData.images) ? resp.jsonData.images : []);
      if (resp.jsonData.companyImageId !== undefined) {
        setSelectedImageId(resp.jsonData.companyImageId || null);
      }
    }
  }

  async function saveCompanyImageAsync(imageId) {
    if (!companyId) return;
    if (updateSpinner) updateSpinner(true);
    let url = `/Company/${companyId}/ImageSelect`;
    if (imageId && imageId > 0) {
      url += `/${imageId}`;
    }
    const resp = await RightTimeApi.putAsync(url);
    if (updateSpinner) updateSpinner(false);
    if (resp && resp.ok) {
      setSelectedImageId(imageId || null);
      if (onClose) onClose();
    }
  }

  async function onSelectNoneAsync() {
    await saveCompanyImageAsync(null);
  }

  async function handleSelectImage(imageId) {
    await saveCompanyImageAsync(imageId);
  }

  async function uploadImagesAsync(files) {
    const fileList = Array.from(files || []);
    if (fileList.length === 0 || !companyId) return;
    if (updateSpinner) updateSpinner(true);

    try {
      await Promise.all(
        fileList.map((file) => RightTimeApi.postFileAsync(`/Company/${companyId}/Image`, file))
      );
      toast.success("Images uploaded successfully");
      getImageAsync();
    } catch (error) {
      console.error("Error uploading images:", error);
      if (error?.status === 401) {
        toast.error("Server rejected authentication for image upload.");
      } else {
        toast.error(`Failed to upload images: ${error?.message || "Unknown error"}`);
      }
    } finally {
      if (updateSpinner) updateSpinner(false);
    }
  }

  function handleFileChange(e) {
    uploadImagesAsync(e.target.files);
    e.target.value = "";
  }

  function handleDragOver(e) {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  }

  function handleDragLeave(e) {
    e.preventDefault();
    e.stopPropagation();
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setIsDragOver(false);
    }
  }

  function handleDrop(e) {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    uploadImagesAsync(e.dataTransfer?.files);
  }


  async function onDeleteFileAsync(e) {
    const imageId = e.currentTarget.getAttribute("data-imageid");
    const resp = await RightTimeApi.deleteAsync(`/Image/${imageId}`);
    if (resp && resp.ok) {
      toast.success("Image deleted successfully");
      getImageAsync();
    } else {
      toast.error("Failed to delete image");
    }
  }

  if (!isOpen) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Select Company Image"
      className="imagePickerModal"
    >
      <div
        className="modalBody"
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        style={{
          border: isDragOver ? "2px dashed #4b89dc" : "2px dashed transparent",
          borderRadius: "6px",
        }}
      >
        <div className="tableWrapper dimensionTable inline-block">
          <table>
            <caption>
              <span className="title">Images</span>
              <div className="captionRight">
              <FaPlusSquare
                className="actionIcon plusIcon"
                onClick={() => fileInputRef.current?.click()}
              />
              </div>
            </caption>
            <thead>
              <tr>
                <th></th>
                <th>Id</th>
                <th>FileName</th>
                <th>Image</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {images && images.length > 0
                ? images.map((image) => (
                    <tr key={image.id}>
                      <td>
                        <input
                          type="radio"
                          name="selectedCompanyImage"
                          checked={selectedImageId === image.id}
                          onChange={() => handleSelectImage(image.id)}
                        />
                      </td>
                      <td>{image.id}</td>
                      <td>{image.fileName || image.name}</td>
                      <td>
                        {image && image.id ? (
                          <PickerImageThumbnail
                            image={image}
                            onSelect={handleSelectImage}
                          />
                        ) : null}
                      </td>
                      <td>
                        <IoTrashOutline data-imageid={image.id} onClick={onDeleteFileAsync} className="actionIcon trashIcon" />
                      </td>
                    </tr>
                  ))
                : null}
            </tbody>
          </table>
        </div>
        <input
          type="file"
          name="image"
          multiple={true}
          ref={fileInputRef}
          onChange={handleFileChange}
          style={{ display: "none" }}
        />
      </div>
    </Modal>
  );
}


